import { useState } from 'react';
import { ButtonShell, ControlledPopover, Image } from '@round/ui-kit';
import { ActionCard, assets } from '@round/whitelabel-ui';
import cn from 'classnames';
import uniq from 'lodash/uniq';
import { useClient, usePartnershipsQueriesByIds } from '@round/creatorbase-helpers/api-hooks';
import useOnClickOutside from 'Hooks/useOnClickOutside';
import useProjectDetails from '../../../../useProjectDetails';
import LinkedAgenciesCard from '../LinkedAgenciesCard/LinkedAgenciesCard';
import LinkedProjectsPopout from '../LinkedProjectsPopout/LinkedProjectsPopout';
import popoutStyles from '../../popout.module.css';
import styles from './OwnProjectRunBy.module.css';

const { Pencil: PencilIcon } = assets;

const OwnProjectRunBy = () => {
    const { project, isLoading, team, isTeamLoading } = useProjectDetails();
    const { client, isLoading: isClientLoading } = useClient(team?.client_id);
    const runByPartnershipIds = uniq(project?.run_by_partnership_ids ?? []);
    const { linkedAgencies, areLinkedAgenciesLoading } = usePartnershipsQueriesByIds([runByPartnershipIds], {
        combine: (queries) => ({
            linkedAgencies: queries.flatMap((query) => query.data?.results ?? []),
            areLinkedAgenciesLoading: queries.some((query) => query.isLoading),
        }),
    });

    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const [popout, setPopout] = useState<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    useOnClickOutside([anchor, popout], () => setIsOpen(false));

    const isRunByLoading = isLoading || isTeamLoading || isClientLoading;

    if (!project || isRunByLoading || !runByPartnershipIds.length) {
        return (
            <ActionCard
                className={styles.card}
                title={client?.name ?? '-'}
                isLoading={isRunByLoading}
                Icon={({ className }) => (
                    <Image className={cn(className, styles.logo)} src={client?.logo ?? ''} alt={client?.name} />
                )}
            />
        );
    }

    const handleClose = () => {
        setIsOpen(false);
        anchor?.focus();
    };

    return (
        <>
            <ButtonShell
                ref={setAnchor}
                className={popoutStyles.trigger}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((isOpen) => !isOpen)}
            >
                <LinkedAgenciesCard
                    name={client?.name}
                    logo={client?.logo}
                    agencies={linkedAgencies}
                    areAgenciesLoading={areLinkedAgenciesLoading}
                />
                <PencilIcon className={popoutStyles.editIcon} aria-hidden />
            </ButtonShell>

            <ControlledPopover
                isOpen={isOpen}
                anchorElement={anchor}
                options={{
                    placement: 'bottom-end',
                    modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
                }}
                overlayClassName={popoutStyles.overlay}
            >
                <div
                    ref={setPopout}
                    className={popoutStyles.popout}
                    role="dialog"
                    aria-label="Linked projects"
                    tabIndex={-1}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleClose();
                        }
                    }}
                >
                    <LinkedProjectsPopout projectId={project.id} onClose={handleClose} />
                </div>
            </ControlledPopover>
        </>
    );
};

export default OwnProjectRunBy;
